import { useMemo, useEffect } from 'react';
import { useOnboardingStore } from '@/store/onboardingStore';
import { useAthleteStore } from '@/store';
import { ProgressBar } from './components/ProgressBar';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { RoleScreen } from './screens/RoleScreen';
// Deep Athlete Flow
import { MatIdentityScreen } from './screens/MatIdentityScreen';
import { AttackScreen } from './screens/AttackScreen';
import { DefenseScreen } from './screens/DefenseScreen';
import { TrainingSetupScreen } from './screens/TrainingSetupScreen';
import { WeightScreen } from './screens/WeightScreen';
import { ScheduleScreen } from './screens/ScheduleScreen';
import { VideoAnalysisScreen } from './screens/VideoAnalysisScreen';
import { ProjectionsScreen } from './screens/ProjectionsScreen';
// Role Specific
import { ParentSetupScreen, CoachSetupScreen } from './screens/RoleSpecificScreens';

/* ─── Onboarding Wizard ─────────────────────────────────────── */

export function OnboardingWizard() {
    const { step, loaded, hydrate, prevStep, profile } = useOnboardingStore();
    const athleteId = useAthleteStore((s) => s.activeId);

    useEffect(() => {
        hydrate(athleteId);
    }, [athleteId, hydrate]);

    const screens = useMemo(() => {
        const common = [
            <WelcomeScreen key="welcome" />,
            <RoleScreen key="role" />,
        ];

        if (profile.role === 'parent') {
            return [...common, <ParentSetupScreen key="parent" />];
        }

        if (profile.role === 'coach') {
            return [...common, <CoachSetupScreen key="coach" />];
        }

        // Default: Athlete Deep Flow
        return [
            ...common,
            <MatIdentityScreen key="identity" />,
            <AttackScreen key="attack" />,
            <DefenseScreen key="defense" />,
            <TrainingSetupScreen key="setup" />,
            <WeightScreen key="weight" />,
            <ScheduleScreen key="schedule" />,
            <VideoAnalysisScreen key="video" />,
            <ProjectionsScreen key="projections" />,
        ];
    }, [profile.role]);

    if (!loaded) {
        return (
            <div className="min-h-screen bg-bg flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg text-text-primary">
            <div className="max-w-md mx-auto px-4 pt-8 pb-12">
                {/* Progress bar */}
                {step > 0 && (
                    <div className="mb-8">
                        <ProgressBar current={step} total={screens.length} />
                    </div>
                )}

                {/* Back button */}
                {step > 0 && (
                    <button
                        onClick={prevStep}
                        className="flex items-center gap-1.5 text-text-muted text-sm mb-6 hover:text-text-secondary transition-colors"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                        Back
                    </button>
                )}

                {/* Current screen */}
                <div className="animate-[fadeIn_0.3s_ease-out]">
                    {screens[step]}
                </div>
            </div>
        </div>
    );
}
